// Deterministic recommendation engine: detected signals -> pinpointed Common Ninja
// widgets + matching free tools + a cited stat. The LLM (when enabled) only adds a
// business-type label; this table is the source of truth. All widget slugs are
// verified against apps.json; all free-tool paths against src/app/(general)/free-tools.

import { BENCHMARKS } from './benchmarks';

export type CategoryKey =
  | 'seo'
  | 'trust'
  | 'conversion'
  | 'engagement';

export const CATEGORY_LABELS: Record<CategoryKey, string> = {
  seo: 'SEO & Mobile',
  trust: 'Trust & Social Proof',
  conversion: 'Conversion',
  engagement: 'Engagement',
};

/**
 * Plain-language explanation of how each category score is calculated, surfaced
 * in a tooltip on the rating cards so the numbers are transparent, not magic.
 */
export const CATEGORY_METHODOLOGY: Record<CategoryKey, string> = {
  seo: 'Share of on-page SEO essentials we detected: a title tag, meta description, Open Graph tags, structured data (schema), a favicon, HTTPS, and a mobile viewport.',
  trust: 'Starts at a 50 baseline, then adjusts for the trust signals we found: testimonials, third-party review-site presence, and linked social profiles. We deduct points if you take payments on-site but show no trust or payment badges.',
  conversion: 'Share of the four core conversion paths present on the page: a contact/lead form, email capture, live chat, and an FAQ section.',
  engagement: 'Based on how many social platforms you actively link to. More connected channels mean more ways to keep visitors engaged with fresh content.',
};

/** Signals extracted from the analyzed page (see detect.ts). */
export interface ISiteSignals {
  url: string;
  finalUrl: string;
  title: string;
  metaDescription: string;
  hasViewport: boolean;
  hasFavicon: boolean;
  hasOgTags: boolean;
  hasSchema: boolean;
  isHttps: boolean;
  hasContactForm: boolean;
  hasFaq: boolean;
  hasTestimonials: boolean;
  hasEmailCapture: boolean;
  hasChat: boolean;
  hasCountdown: boolean;
  hasTrustBadges: boolean;
  hasPayments: boolean;
  isEcommerce: boolean;
  /** Review platforms the brand is present on, e.g. ['trustpilot','google']. */
  reviewPlatforms: string[];
  /** Social platforms the brand links to, e.g. ['instagram','facebook']. */
  socialPlatforms: string[];
  /** Detected tech (CMS, platform, existing third-party tools). */
  techStack: string[];
  /** Heuristic or LLM-derived: 'ecommerce' | 'saas' | 'local' | 'content' | 'general'. */
  businessType: string;
}

export interface IRecommendation {
  id: string;
  category: CategoryKey;
  /** Higher = more important. Used for ordering. */
  priority: number;
  title: string;
  /** Why we recommend this. References what we actually detected. */
  finding: string;
  /** Common Ninja widget slugs (looked up in apps.json for name + logo). */
  widgetSlugs: string[];
  /** Matching free-tool slugs. */
  freeToolSlugs: string[];
  /** Benchmark id from benchmarks.ts (optional; SEO/favicon fixes have no stat). */
  statId?: string;
}

export interface IFreeTool {
  slug: string;
  category: string; // free-tools category path segment
  name: string;
  description: string;
}

// Free-tool catalog (paths verified against src/app/(general)/free-tools/<category>/<slug>).
export const FREE_TOOLS: Record<string, IFreeTool> = {
  'conversion-rate-calculator': { slug: 'conversion-rate-calculator', category: 'conversion-traffic', name: 'Conversion Rate Calculator', description: 'See your CVR vs. industry benchmarks and the revenue at stake.' },
  'bounce-rate-calculator': { slug: 'bounce-rate-calculator', category: 'conversion-traffic', name: 'Bounce Rate Calculator', description: 'Find out if your bounce rate is costing you conversions.' },
  'cart-abandonment-calculator': { slug: 'cart-abandonment-calculator', category: 'ecommerce', name: 'Cart Abandonment Calculator', description: 'Quantify the revenue you lose to abandoned carts.' },
  'roas-calculator': { slug: 'roas-calculator', category: 'ecommerce', name: 'ROAS Calculator', description: 'Measure the return on your ad spend.' },
  'review-request-generator': { slug: 'review-request-generator', category: 'reviews', name: 'Review Request Generator', description: 'Generate messages that get more customers to leave reviews.' },
  'social-proof-roi-calculator': { slug: 'social-proof-roi-calculator', category: 'reviews', name: 'Social Proof ROI Calculator', description: 'Estimate the conversion lift from adding social proof.' },
  'nps-calculator': { slug: 'nps-calculator', category: 'reviews', name: 'NPS Calculator', description: 'Calculate your Net Promoter Score from survey responses.' },
  'cpl-calculator': { slug: 'cpl-calculator', category: 'lead-generation', name: 'Cost Per Lead Calculator', description: 'Work out what each lead actually costs you.' },
  'popup-roi-calculator': { slug: 'popup-roi-calculator', category: 'lead-generation', name: 'Popup ROI Calculator', description: 'Project the email signups and revenue a popup can add.' },
  'form-conversion-calculator': { slug: 'form-conversion-calculator', category: 'lead-generation', name: 'Form Conversion Calculator', description: 'See how form length and fields affect your conversion.' },
  'newsletter-signup-calculator': { slug: 'newsletter-signup-calculator', category: 'lead-generation', name: 'Newsletter Signup Calculator', description: 'Estimate list growth from your traffic and signup rate.' },
  'meta-tag-generator': { slug: 'meta-tag-generator', category: 'seo', name: 'Meta Tag Generator', description: 'Generate title and meta description tags for your pages.' },
  'og-tag-generator': { slug: 'og-tag-generator', category: 'seo', name: 'Open Graph Tag Generator', description: 'Create OG tags so your links look great when shared.' },
  'faq-schema-generator': { slug: 'faq-schema-generator', category: 'seo', name: 'FAQ Schema Generator', description: 'Generate FAQPage structured data for your FAQ section.' },
  'engagement-rate-calculator': { slug: 'engagement-rate-calculator', category: 'social-media', name: 'Engagement Rate Calculator', description: 'Measure how engaged your social audience really is.' },
  'favicon-generator': { slug: 'favicon-generator', category: 'website-tools', name: 'Favicon Generator', description: 'Create a favicon so your site looks complete in browser tabs.' },
};

export function getFreeTool(slug: string): IFreeTool | undefined {
  return FREE_TOOLS[slug];
}
export function freeToolPath(tool: IFreeTool): string {
  return `/free-tools/${tool.category}/${tool.slug}`;
}

// Map a detected review platform to the matching Common Ninja reviews widget.
// Verified slugs; falls back to all-in-one-reviews when there's no exact match.
const REVIEW_WIDGET: Record<string, string> = {
  google: 'google-reviews',
  yelp: 'yelp-reviews',
  g2: 'g2-reviews',
  capterra: 'capterra-reviews',
  tripadvisor: 'tripadvisor-reviews',
  glassdoor: 'glassdoor-reviews',
  facebook: 'facebook-reviews',
  trustpilot: 'reviews-trust-box', // no Trustpilot-specific widget; Trust Box is the closest
};

const REVIEW_LABEL: Record<string, string> = {
  google: 'Google', yelp: 'Yelp', g2: 'G2', capterra: 'Capterra',
  tripadvisor: 'Tripadvisor', glassdoor: 'Glassdoor', facebook: 'Facebook',
  trustpilot: 'Trustpilot',
};

// Map a detected social platform to its Common Ninja feed widget (verified slugs).
const SOCIAL_FEED_WIDGET: Record<string, string> = {
  instagram: 'instagram-feed',
  facebook: 'facebook-feed',
  tiktok: 'tiktok-feed',
  youtube: 'youtube-feed',
  linkedin: 'linkedin-feed',
  twitter: 'twitter-feed',
  pinterest: 'pinterest-feed',
  threads: 'threads-feed',
};

const SOCIAL_LABEL: Record<string, string> = {
  instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok',
  youtube: 'YouTube', linkedin: 'LinkedIn', twitter: 'X (Twitter)',
  pinterest: 'Pinterest', threads: 'Threads',
};

/**
 * The core: turn detected signals into pinpointed, stat-backed recommendations.
 * Pure and deterministic. Already-present capabilities (via techStack) are skipped.
 */
export function generateRecommendations(s: ISiteSignals): IRecommendation[] {
  const recs: IRecommendation[] = [];
  const tech = s.techStack.map((t) => t.toLowerCase());
  const has = (kw: string) => tech.some((t) => t.includes(kw));

  // 1. Review-platform presence -> the EXACT matching reviews widget. The pinpoint.
  if (s.reviewPlatforms.length > 0) {
    const widgets = Array.from(
      new Set(s.reviewPlatforms.map((p) => REVIEW_WIDGET[p]).filter(Boolean))
    );
    widgets.push('all-in-one-reviews');
    const labels = s.reviewPlatforms.map((p) => REVIEW_LABEL[p] || p);
    recs.push({
      id: 'reviews',
      category: 'trust',
      priority: 100,
      title: `Show your ${labels.join(' & ')} reviews on your site`,
      finding: `We found your business listed on ${labels.join(', ')}, but those ratings aren't embedded on your own pages, so visitors have to leave to see them.`,
      widgetSlugs: Array.from(new Set(widgets)),
      freeToolSlugs: ['review-request-generator', 'social-proof-roi-calculator'],
      statId: 'reviews_powerreviews',
    });
  }

  // 2. Active social presence -> the EXACT matching feed widget.
  if (s.socialPlatforms.length > 0) {
    const widgets = s.socialPlatforms.map((p) => SOCIAL_FEED_WIDGET[p]).filter(Boolean);
    const labels = s.socialPlatforms.map((p) => SOCIAL_LABEL[p] || p);
    if (widgets.length > 0) {
      recs.push({
        id: 'social-feed',
        category: 'engagement',
        priority: 80,
        title: `Embed your live ${labels.slice(0, 2).join(' & ')} feed`,
        finding: `Your site links out to ${labels.join(', ')}, but doesn't show that content on-page. An embedded feed keeps visitors engaged with fresh posts instead of sending them away.`,
        widgetSlugs: Array.from(new Set(widgets)).slice(0, 3),
        freeToolSlugs: ['engagement-rate-calculator'],
        statId: 'social_creator_bazaarvoice',
      });
    }
  }

  // 3. No testimonials AND no external review presence -> seed social proof.
  if (!s.hasTestimonials && s.reviewPlatforms.length === 0) {
    recs.push({
      id: 'testimonials',
      category: 'trust',
      priority: 75,
      title: 'Add customer testimonials',
      finding: "We didn't find testimonials or a reviews section. New visitors look for proof that other people trust you before they act.",
      widgetSlugs: ['testimonials', 'reviews-trust-box'],
      freeToolSlugs: ['review-request-generator', 'nps-calculator'],
      statId: 'reviews_powerreviews',
    });
  }

  // 4. No FAQ section.
  if (!s.hasFaq) {
    recs.push({
      id: 'faq',
      category: 'conversion',
      priority: 60,
      title: 'Add an FAQ section',
      finding: "No FAQ or Q&A section was detected. Answering common objections on the page removes friction and cuts repetitive support questions.",
      widgetSlugs: ['faq', 'accordion'],
      freeToolSlugs: ['faq-schema-generator'],
      statId: 'faq_qa_74',
    });
  }

  // 5. No contact / lead form.
  if (!s.hasContactForm) {
    recs.push({
      id: 'contact-form',
      category: 'conversion',
      priority: 70,
      title: 'Add a contact or lead form',
      finding: "We couldn't find a contact or lead-capture form. A short, well-placed form is the simplest way to turn interest into a conversation.",
      widgetSlugs: ['contact-form', 'form-builder'],
      freeToolSlugs: ['cpl-calculator', 'form-conversion-calculator'],
      statId: 'forms_baymard',
    });
  }

  // 6. No email capture / popup (skip if an email tool is already detected).
  if (!s.hasEmailCapture && !has('mailchimp') && !has('klaviyo') && !has('optinmonster')) {
    recs.push({
      id: 'email-capture',
      category: 'conversion',
      priority: 65,
      title: 'Capture emails with a popup',
      finding: "No email-capture or newsletter signup was detected. A timed or exit-intent popup turns leaving visitors into subscribers you can re-engage.",
      widgetSlugs: ['popup-builder', 'email-subscription-form'],
      freeToolSlugs: ['popup-roi-calculator', 'newsletter-signup-calculator'],
      statId: 'popup_omnisend',
    });
  }

  // 7. No chat (skip if an established chat provider is already on the page).
  if (!s.hasChat && !has('intercom') && !has('drift') && !has('tidio') && !has('crisp') && !has('tawk') && !has('zendesk')) {
    recs.push({
      id: 'chat',
      category: 'conversion',
      priority: 55,
      title: 'Add live chat or a chatbot',
      finding: "No chat or instant-contact option was detected. Letting visitors get an answer without leaving the page keeps high-intent users from bouncing.",
      widgetSlugs: ['ai-chatbot', 'whatsapp-button', 'all-in-one-chat-button'],
      freeToolSlugs: [],
    });
  }

  // 8. E-commerce conversion boosters.
  if (s.isEcommerce) {
    recs.push({
      id: 'ecommerce-conversion',
      category: 'conversion',
      priority: 50,
      title: 'Boost store conversions',
      finding: 'Your site looks like an online store. Urgency, clear pricing, and localized currency reduce hesitation at the moment of purchase.',
      widgetSlugs: ['countdown', 'pricing-tables', 'currency-converter'],
      freeToolSlugs: ['cart-abandonment-calculator', 'roas-calculator'],
      statId: 'cart_baymard',
    });

    // 8b. Payments present but no trust badges.
    if (s.hasPayments && !s.hasTrustBadges) {
      recs.push({
        id: 'trust-badges',
        category: 'trust',
        priority: 58,
        title: 'Add trust & payment badges near checkout',
        finding: 'You take payments on-site but no trust or payment-method badges were detected. Visible security cues at the payment step reduce card-entry anxiety.',
        widgetSlugs: ['trust-badges', 'payment-method-logos'],
        freeToolSlugs: [],
        statId: 'trust_card_19',
      });
    }
  }

  // 9. SEO basics (meta / OG / schema).
  if (!s.metaDescription || !s.hasOgTags || !s.hasSchema) {
    const missing: string[] = [];
    if (!s.metaDescription) missing.push('a meta description');
    if (!s.hasOgTags) missing.push('Open Graph tags');
    if (!s.hasSchema) missing.push('structured data (schema)');
    recs.push({
      id: 'seo-meta',
      category: 'seo',
      priority: 45,
      title: 'Fix missing SEO tags',
      finding: `Your page is missing ${missing.join(', ')}. These control how you appear in search results and when your links are shared.`,
      widgetSlugs: [],
      freeToolSlugs: ['meta-tag-generator', 'og-tag-generator', 'faq-schema-generator'],
    });
  }

  // 11. No favicon.
  if (!s.hasFavicon) {
    recs.push({
      id: 'favicon',
      category: 'seo',
      priority: 20,
      title: 'Add a favicon',
      finding: "No favicon was detected. The little icon in the browser tab is a small but real signal of a polished, trustworthy site.",
      widgetSlugs: [],
      freeToolSlugs: ['favicon-generator'],
    });
  }

  return recs.sort((a, b) => b.priority - a.priority);
}

export interface ICategoryScore {
  key: CategoryKey;
  label: string;
  /** 0-100. */
  score: number;
  status: 'poor' | 'fair' | 'good';
}

function statusFor(score: number): ICategoryScore['status'] {
  if (score >= 75) return 'good';
  if (score >= 50) return 'fair';
  return 'poor';
}

function pct(haves: boolean[]): number {
  const total = haves.length;
  const got = haves.filter(Boolean).length;
  return Math.round((got / total) * 100);
}

/** Transparent 0-100 scores per category + a blended overall. */
export function scoreReport(
  s: ISiteSignals
): { overall: number; categories: ICategoryScore[] } {
  // SEO: heuristic from the on-page tags we can detect.
  const seo = pct([
    !!s.title,
    !!s.metaDescription,
    s.hasOgTags,
    s.hasSchema,
    s.hasFavicon,
    s.isHttps,
    s.hasViewport,
  ]);

  const conversion = pct([
    s.hasContactForm,
    s.hasEmailCapture,
    s.hasChat,
    s.hasFaq,
  ]);

  let trust = 50;
  if (s.hasTestimonials) trust += 20;
  if (s.reviewPlatforms.length > 0) trust += 15;
  if (s.socialPlatforms.length > 0) trust += 15;
  if (s.isEcommerce && s.hasPayments && !s.hasTrustBadges) trust -= 15;
  trust = Math.max(0, Math.min(100, trust));

  const engagement = Math.max(
    0,
    Math.min(100, 40 + s.socialPlatforms.length * 20)
  );

  const categories: ICategoryScore[] = [
    { key: 'seo', label: CATEGORY_LABELS.seo, score: seo, status: statusFor(seo) },
    { key: 'trust', label: CATEGORY_LABELS.trust, score: trust, status: statusFor(trust) },
    { key: 'conversion', label: CATEGORY_LABELS.conversion, score: conversion, status: statusFor(conversion) },
    { key: 'engagement', label: CATEGORY_LABELS.engagement, score: engagement, status: statusFor(engagement) },
  ];

  const overall = Math.round(
    categories.reduce((a, c) => a + c.score, 0) / categories.length
  );

  return { overall, categories };
}
